import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { linkageApi, scenarioStorageApi, ScenarioListItem, VersionMetadata } from '../services/api';

interface AUScenarioLinkageEditDialogProps {
  open: boolean;
  onClose: () => void;
  bucketId: string;
  environmentId: string;
  packageKey: string;
  auId: string;
  auTitle?: string;
  currentLinkage?: string; // Format: "scenarioUuid:version"
  onSaved?: () => void;
}

export const AUScenarioLinkageEditDialog: React.FC<AUScenarioLinkageEditDialogProps> = ({
  open,
  onClose,
  bucketId,
  environmentId,
  packageKey,
  auId,
  auTitle,
  currentLinkage,
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [scenarios, setScenarios] = useState<ScenarioListItem[]>([]);
  const [selectedScenarioUuid, setSelectedScenarioUuid] = useState<string>('');
  const [versions, setVersions] = useState<VersionMetadata[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Load available scenarios when dialog opens
  useEffect(() => {
    if (open) {
      loadScenarios();
    }
  }, [open, bucketId, environmentId]);

  // Load versions when scenario is selected
  useEffect(() => {
    if (selectedScenarioUuid) {
      loadVersions(selectedScenarioUuid);
    } else {
      setVersions([]);
      setSelectedVersion('');
    }
  }, [selectedScenarioUuid]);

  // Initialize selection from current linkage
  useEffect(() => {
    if (currentLinkage && open) {
      const [scenarioUuid, version] = currentLinkage.split(':');
      setSelectedScenarioUuid(scenarioUuid);
      setSelectedVersion(version);
    } else if (open) {
      setSelectedScenarioUuid('');
      setSelectedVersion('');
    }
  }, [currentLinkage, open]);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const scenarioList = await scenarioStorageApi.listScenarios(bucketId, environmentId);
      setScenarios(scenarioList);
    } catch (err) {
      console.error('Error loading scenarios:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async (scenarioUuid: string) => {
    try {
      setLoadingVersions(true);
      const versionList = await scenarioStorageApi.getScenarioVersions(bucketId, environmentId, scenarioUuid);
      // Sort versions by timestamp (newest first)
      versionList.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setVersions(versionList);

      // Auto-select current version if not already set
      if (!selectedVersion && versionList.length > 0) {
        const currentVersion = versionList.find(v => v.isCurrent);
        setSelectedVersion(currentVersion?.version || versionList[0].version);
      }
    } catch (err) {
      console.error('Error loading versions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleSave = async () => {
    if (!selectedScenarioUuid || !selectedVersion) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await linkageApi.setLinkage(
        bucketId,
        environmentId,
        packageKey,
        auId,
        selectedScenarioUuid,
        selectedVersion
      );

      if (onSaved) {
        onSaved();
      }

      onClose();
    } catch (err) {
      console.error('Error saving linkage:', err);
      setError(err instanceof Error ? err.message : 'Failed to save linkage');
    } finally {
      setSaving(false);
    }
  };

  const getScenarioDisplayName = (scenario: ScenarioListItem): string => {
    if (scenario.metadata?.scenarioInfo?.name) {
      return scenario.metadata.scenarioInfo.name;
    }
    return scenario.uuid;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Link Scenario to AU
        {auTitle && (
          <Typography variant="body2" color="text.secondary">
            {auTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : scenarios.length === 0 ? (
          <Alert severity="info">
            No RangeOS scenario drafts found in this environment. Export scenarios from RangeOS first.
          </Alert>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Scenario</InputLabel>
              <Select
                value={selectedScenarioUuid}
                onChange={(e) => setSelectedScenarioUuid(e.target.value)}
                label="Select Scenario"
              >
                {scenarios.map((scenario) => (
                  <MenuItem key={scenario.uuid} value={scenario.uuid}>
                    {getScenarioDisplayName(scenario)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedScenarioUuid || loadingVersions}>
              <InputLabel>Select Version</InputLabel>
              <Select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                label="Select Version"
              >
                {loadingVersions ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading versions...
                  </MenuItem>
                ) : versions.length === 0 ? (
                  <MenuItem disabled>No versions available</MenuItem>
                ) : (
                  versions.map((version) => (
                    <MenuItem key={version.version} value={version.version}>
                      {version.version}
                      {version.isCurrent && ' (current)'}
                      {' - '}
                      {new Date(version.timestamp).toLocaleString()}
                      {version.description && ` - ${version.description}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {selectedScenarioUuid && selectedVersion && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Will link AU to: {selectedScenarioUuid.slice(0, 16)}... version {selectedVersion}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedScenarioUuid || !selectedVersion || saving || loading}
        >
          {saving ? 'Saving...' : 'Save Linkage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
